import type MockAdapter from 'axios-mock-adapter'
import { auditFieldsForCreate, auditFieldsForUpdate, currentUserId, newId } from '../engine/context'
import { findRow, insertRow, readTable, updateRow } from '../store/localDb.store'
import type { LocalRow } from '../types/localDb.types'
import {
  fail,
  handle,
  idOf,
  nextDocNo,
  parse,
  requireRole,
  roleCodesOf,
  statusCodeOf,
  txLog,
  workflowRunner
} from './c07.shared'
import { systemTransition } from './opname.shared'
import { registerReadHook } from './readHooks'
import { isRepack } from './repackGuard'
import { almAutoApprove } from './slaSetting'

/**
 * Endpoint custom C10 Merge & Konsolidasi FG — `/api/v1/wms/merge-fg/*`:
 * - GAB-01 (WF-GAB-01, entity `merge_request`): `pack-preview`, `alm-start` (simulasi start mesin ALM — demo), `approve`,
 *   `reject` (Tolak — semua jenis, alasan wajib), `alm-rule` (aturan Auto-approve untuk hitung mundur), `create-pack`,
 *   `cancel-pack`. Auto-approve: `autoApproveSweep()` dijalankan setiap `merge_request` dibaca (kait baca).
 * - GAB-02: `pick-gate`, `pick-unit` (Slop / Bal → Area ALM), `pick-pack`, `arrive` (scan Area ALM), `hand-over`
 *   (pack → Area tujuan produksi).
 * - KSL-01 (WF-KSL-01, entity `consolidation_task`): `suggestions`, `create-tasks`, `stop-task`.
 * - KSL-02: `source-gate`, `load-unit`, `carry`, `place`.
 * Mode Kembalikan (ALM batal produksi), sesi Operator ALM dan pesan vendor tidak dibawa ke mvdp (laporan 14).
 */

export const PAL_REC = 18
export const PAL_CAP = 24
const EPS = 0.0001

const gabFlow = workflowRunner('WF-GAB-01', 'merge_request')
const kslFlow = workflowRunner('WF-KSL-01', 'consolidation_task')
const ACTIVE_GAB = ['GAB_PENDING', 'GAB_READY', 'GAB_PICKING']
const ACTIVE_KSL = ['KSL_ASSIGNED', 'KSL_LOADED']

const up = (value: unknown) =>
  String(value ?? '')
    .trim()
    .toUpperCase()
const round3 = (value: number) => Math.round(value * 1000) / 1000
const nowIso = () => new Date().toISOString()
const labelOf = (id: unknown) => findRow('fg_label', idOf(id)) as LocalRow
const skuOf = (id: unknown) => findRow('mt_product', idOf(id)) as LocalRow
const palletOf = (id: unknown) => findRow('pallet', idOf(id))
const palletByCode = (code: string) =>
  readTable('pallet').find(row => row.pallet_kind === 'FG' && up(row.code) === up(code))
const containerByCode = (code: string) => readTable('container').find(row => up(row.code) === up(code))
const batchNoOf = (id: unknown) => String(findRow('fg_labeler_batch', idOf(id))?.batch_no ?? '')
const balPerKarton = (skuId: unknown) => Number(skuOf(skuId)?.bal_per_karton ?? 4) || 4

/** `Bal` bisa ditulis lewat pallet atau container; kode rak dipetakan ke pallet / container FG. */
const rackCode = (item: LocalRow) =>
  String(palletOf(item.from_pallet_id)?.code ?? findRow('container', idOf(item.from_container_id))?.code ?? '')

/** Cocokkan hasil scan dengan label: QR lengkap, ekor QR, atau GTIN. */
const matchesLabel = (label: LocalRow, code: string) => {
  const scanned = up(code)
  const no = up(label.label_no)
  return scanned === no || no.endsWith(`:${scanned}`) || (!!label.gtin && scanned === up(label.gtin))
}

function setPalletOccupied(palletId: unknown, delta: number, fill?: { sku_id: unknown; unit_type: string }) {
  const pallet = palletOf(palletId)
  if (!pallet) return
  const left = Math.max(0, Number(pallet.occupied ?? 0) + delta)
  updateRow('pallet', pallet.id as string, {
    occupied: left,
    ...(left === 0
      ? { sku_id: null, unit_type: null, pallet_status: 'EMPTY' }
      : fill
        ? { sku_id: fill.sku_id, unit_type: fill.unit_type, pallet_status: 'IN_USE' }
        : {}),
    ...auditFieldsForUpdate()
  })
}

/* ============================================================ GAB — permintaan */

const gabOf = (id: unknown) => {
  const row = findRow('merge_request', String(id ?? ''))
  if (!row) fail('Permintaan tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const gabItems = (req: LocalRow) => readTable('merge_request_item').filter(item => idOf(item.request_id) === req.id)

function gabRun(req: LocalRow, action: string, actors: string[], patch: Record<string, unknown> = {}, reason = '') {
  gabFlow.run(req, action, actors, patch, reason)
  const fresh = gabOf(req.id)
  updateRow('merge_request', req.id as string, { merge_status: statusCodeOf(fresh).replace('GAB_', '') })
}

const looseLabels = (skuId: string, unit: 'Slop' | 'Bal') =>
  readTable('fg_label')
    .filter(
      label =>
        idOf(label.sku_id) === skuId &&
        label.level === unit &&
        label.label_status === 'IN_STOCK' &&
        (label.pallet_id || label.container_id)
    )
    .sort(
      (a, b) =>
        batchNoOf(a.batch_id).localeCompare(batchNoOf(b.batch_id)) ||
        String(a.label_no).localeCompare(String(b.label_no))
    )

const freePacks = (skuId: string) =>
  readTable('fg_pack_stock').filter(
    row => idOf(row.sku_id) === skuId && row.status === 'IN_STOCK' && Number(row.qty_pack ?? 0) > 0
  )

const originOf = (label: LocalRow) => (label.is_priority_lot ? 'Retur' : 'Produksi')

function insertItem(req: LocalRow, source: { label?: LocalRow; pack?: LocalRow }) {
  const { label, pack } = source
  return insertRow('merge_request_item', {
    ...auditFieldsForCreate(),
    id: newId(),
    request_id: req.id,
    label_id: label?.id ?? null,
    pack_stock_id: pack?.id ?? null,
    sku_id: idOf((label ?? pack)?.sku_id),
    batch_id: idOf((label ?? pack)?.batch_id),
    unit_type: label ? label.level : 'Pack',
    qty_plan: label ? 1 : Number(pack?.qty_pack ?? 0),
    qty_taken: null,
    /** Pack Good / Repack hasil claim berada di pallet RC (laporan 22), bukan container. */
    from_pallet_id: label?.pallet_id ?? pack?.pallet_id ?? null,
    from_container_id: label?.container_id ?? pack?.container_id ?? null,
    is_return: Boolean(label?.is_priority_lot ?? pack?.is_priority_lot),
    origin: label ? originOf(label) : String(pack?.origin ?? 'Produksi'),
    item_status: 'RESERVED'
  })
}

const destinationOf = (code: string) =>
  readTable('warehouse_zone').find(
    zone =>
      up(zone.code) === up(code) &&
      ['PROD_AREA', 'PRODUCTION_AREA'].includes(String(zone.zone_type)) &&
      zone.is_active !== false
  )
const almAreaCode = (alm: number) => `AREA-ALM-${alm}`

function createRequest(input: {
  kind: 'ALM' | 'PACK'
  jenis: 'Slop' | 'Bal' | 'Pack'
  skuId: string
  alm?: number
  source: string
  destination: string
  operatorId?: string | null
  linked?: string | null
}) {
  const id = newId()
  const pngNo = nextDocNo('GAB', 'merge_request', 'png_no')
  const { statusId, submissionId } = gabFlow.start(id)
  return insertRow('merge_request', {
    ...auditFieldsForCreate(),
    id,
    png_no: pngNo,
    operator_id: input.operatorId ?? null,
    merge_status: 'PENDING',
    status_id: statusId,
    submission_id: submissionId,
    kind: input.kind,
    jenis: input.jenis,
    sku_id: input.skuId,
    alm_no: input.alm ?? null,
    source_text: input.source,
    destination_code: input.destination,
    linked_request_id: input.linked ?? null,
    approved_by_id: null,
    approved_at: null,
    completed_at: null,
    cancel_reason: null
  })
}

/** Simulasi START mesin ALM (demo — di produksi pesan datang dari vendor ALM). */
export function almStart(body: { alm?: number; sku_id?: string }) {
  requireRole('Simulasi start ALM hanya untuk Admin atau Supervisor WH FG.', 'admin_wh_fg', 'supervisor_wh_fg')
  const alm = Number(body.alm)
  if (![1, 2, 3].includes(alm)) fail('ALM harus 1, 2, atau 3.')
  const skuId = String(body.sku_id ?? '')
  if (!skuOf(skuId)) fail('SKU tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  const skuCode = String(skuOf(skuId).code)
  const jenis = alm === 1 ? 'Slop' : alm === 2 ? 'Bal' : null
  if (!jenis) return { state: 'ALLOWED', message: `BOLEH — ALM 3 tidak memicu penggabungan`, request_no: null }
  const active = readTable('merge_request').find(
    req =>
      req.kind === 'ALM' && req.jenis === jenis && idOf(req.sku_id) === skuId && ACTIVE_GAB.includes(statusCodeOf(req))
  )
  if (active)
    return {
      state: 'BLOCKED',
      message: `DIBLOKIR — ${active.png_no} ${String(findRow('wml_status', idOf(active.status_id))?.name ?? '').toLowerCase()}`,
      request_no: active.png_no
    }
  const labels = looseLabels(skuId, jenis)
  if (!labels.length)
    return { state: 'ALLOWED', message: `BOLEH — tidak ada ${jenis.toLowerCase()} sisa ${skuCode}`, request_no: null }
  const req = createRequest({
    kind: 'ALM',
    jenis,
    skuId,
    alm,
    source: `ALM ${alm}`,
    destination: almAreaCode(alm)
  })
  labels.forEach(label => {
    insertItem(req, { label })
    updateRow('fg_label', label.id as string, { label_status: 'MERGE_RESERVED', ...auditFieldsForUpdate() })
  })
  txLog(
    'FG Penggabungan',
    'GAB-01',
    'gab-request',
    String(req.png_no),
    `Start ALM ${alm}: ${labels.length} ${jenis} sisa ${skuCode} — ALM diblokir, menunggu persetujuan`
  )
  return {
    state: 'BLOCKED',
    message: `DIBLOKIR — masih ada ${labels.length} ${jenis} sisa ${skuCode}. Permintaan ${req.png_no} menunggu persetujuan Admin`,
    request_no: req.png_no
  }
}

/** Aturan Auto-approve ALM (Master SLA `GAB_ALM_AUTO_APPROVE`) — dibaca GAB-01 / GAB-02 untuk hitung mundur. */
export function almRule() {
  const rule = almAutoApprove()
  return { enabled: rule.enabled, minutes: rule.minutes, value: rule.value, unit: rule.unit, now: nowIso() }
}

/**
 * Auto-approve v7.50 (A-01): permintaan ALM yang masih Menunggu persetujuan lewat batas Master SLA disetujui sistem
 * (`ACT_GAB_AUTO_APPROVE`, tanpa aktor manusia); Supervisor hanya diberi tahu (`GAB_AUTO_APPROVED`, dikirim aturan `WF-GAB-01:ACT_GAB_AUTO_APPROVE` lewat `systemTransition`; `ALM_AUTO_APPROVED` tetap untuk batch Auto Labeler). Aturan yang dimatikan
 * tidak pernah berjalan — langkah menunggu Admin. Dipanggil setiap `merge_request` dibaca, jadi permintaan yang sudah lewat
 * batas saat halaman dibuka langsung diproses.
 */
export function autoApproveSweep(now = Date.now()) {
  const rule = almAutoApprove()
  const approved: string[] = []
  if (!rule.enabled) return { approved }
  readTable('merge_request')
    .filter(req => statusCodeOf(req) === 'GAB_PENDING' && req.kind === 'ALM')
    .filter(req => now - Date.parse(String(req.date_created)) >= rule.minutes * 60_000)
    .forEach(req => {
      systemTransition('WF-GAB-01', req, 'ACT_GAB_AUTO_APPROVE', 'Disetujui otomatis oleh sistem')
      updateRow('merge_request', req.id as string, {
        merge_status: 'READY',
        approved_by_id: null,
        approved_at: new Date(now).toISOString()
      })
      txLog(
        'FG Penggabungan',
        'GAB-01',
        'gab-auto-setuju',
        String(req.png_no),
        `Disetujui otomatis oleh sistem — melewati batas ${rule.value} ${rule.unit === 'HOUR' ? 'jam' : 'menit'} (Master SLA)`
      )
      approved.push(String(req.png_no))
    })
  return { approved }
}

export function approve(body: { request_id?: string }) {
  requireRole('Persetujuan dilakukan Admin WH FG.', 'admin_wh_fg')
  autoApproveSweep()
  const req = gabOf(body.request_id)
  if (statusCodeOf(req) !== 'GAB_PENDING' || req.kind !== 'ALM')
    fail('Permintaan ini sudah tidak menunggu persetujuan.', 'ERR_INVALID_STATE')
  gabRun(req, 'ACT_GAB_APPROVE', ['ACT_GAB_ADMIN_FG'], {
    approved_by_id: currentUserId(),
    approved_at: nowIso()
  })
  txLog('FG Penggabungan', 'GAB-01', 'gab-setuju', String(req.png_no), 'Permintaan disetujui — tugas ambil terbit')
  return { png_no: req.png_no, status: statusCodeOf(gabOf(req.id)) }
}

/** Tolak (v7.50 A-02): semua jenis (Slop / Bal), alasan wajib; barang dilepas dan blokir ALM dibuka. */
export function reject(body: { request_id?: string; reason?: string }) {
  requireRole('Penolakan permintaan dilakukan Admin WH FG.', 'admin_wh_fg')
  autoApproveSweep()
  const req = gabOf(body.request_id)
  if (statusCodeOf(req) !== 'GAB_PENDING' || req.kind !== 'ALM')
    fail('Permintaan ini sudah tidak menunggu persetujuan.', 'ERR_INVALID_STATE')
  const reason = String(body.reason ?? '').trim()
  if (reason.length < 3) fail('Alasan wajib diisi (minimal 3 huruf).', 'ERR_REASON_REQUIRED')
  gabRun(req, 'ACT_GAB_REJECT', ['ACT_GAB_ADMIN_FG'], { cancel_reason: reason }, reason)
  let released = 0
  gabItems(req).forEach(item => {
    updateRow('merge_request_item', item.id as string, { item_status: 'RELEASED', ...auditFieldsForUpdate() })
    const label = item.label_id ? labelOf(item.label_id) : null
    if (label?.label_status === 'MERGE_RESERVED') {
      updateRow('fg_label', label.id as string, { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
      released += 1
    }
    if (item.pack_stock_id)
      updateRow('fg_pack_stock', idOf(item.pack_stock_id), { status: 'IN_STOCK', ...auditFieldsForUpdate() })
  })
  txLog(
    'FG Penggabungan',
    'GAB-01',
    'gab-tolak',
    String(req.png_no),
    `Permintaan ditolak — ${reason}. ${released} ${String(req.jenis)} dilepas, ALM dapat start kembali`
  )
  return { png_no: req.png_no, released }
}

/** Pratinjau pack sisa bebas untuk modal Buat Permintaan Pack. */
export function packPreview(body: { sku_id?: string }) {
  const packs = readTable('fg_pack_stock').filter(row => row.status === 'IN_STOCK' && Number(row.qty_pack ?? 0) > 0)
  const skuIds = [...new Set(packs.map(row => idOf(row.sku_id)))]
  const skus = skuIds
    .map(id => skuOf(id))
    .filter(Boolean)
    .map(sku => ({ id: sku.id, code: sku.code, name: sku.name }))
    .sort((a, b) => String(a.code).localeCompare(String(b.code)))
  const skuId = String(body.sku_id ?? '')
  const rows = skuId
    ? freePacks(skuId).map(row => ({
        id: row.id,
        label_no: row.label_no,
        batch_no: batchNoOf(row.batch_id),
        container: String(
          findRow('container', idOf(row.container_id))?.code ?? findRow('pallet', idOf(row.pallet_id))?.code ?? ''
        ),
        qty: Number(row.qty_pack),
        origin: row.origin
      }))
    : []
  const active = skuId
    ? readTable('merge_request').find(
        req =>
          req.kind === 'PACK' && idOf(req.sku_id) === skuId && ['GAB_READY', 'GAB_PICKING'].includes(statusCodeOf(req))
      )
    : undefined
  const total = rows.reduce((sum, row) => sum + row.qty, 0)
  const repack = rows.filter(row => isRepack(row)).reduce((sum, row) => sum + row.qty, 0)
  return {
    skus,
    rows,
    total,
    /** Pack Repack di dalam total (v7.50 A-03) — ikut Permintaan Gabungan, tidak pernah ke order Pack tunggal. */
    repack,
    good: total - repack,
    active_request: active
      ? {
          id: active.id,
          png_no: active.png_no,
          assignee_id: idOf(active.operator_id),
          destination_code: active.destination_code
        }
      : null
  }
}

export function createPack(body: {
  sku_id?: string
  assignee_id?: string
  destination_code?: string
  linked_request_id?: string
}) {
  requireRole('Permintaan pack dibuat Admin WH FG.', 'admin_wh_fg')
  const skuId = String(body.sku_id ?? '')
  if (!skuOf(skuId)) fail('Pilih SKU dulu.')
  const rows = freePacks(skuId)
  if (!rows.length) fail('Tidak ada pack sisa yang bebas untuk SKU ini.', 'ERR_NO_STOCK')
  const active = readTable('merge_request').find(
    req => req.kind === 'PACK' && idOf(req.sku_id) === skuId && ['GAB_READY', 'GAB_PICKING'].includes(statusCodeOf(req))
  )
  if (active) {
    rows.forEach(pack => {
      insertItem(active, { pack })
      updateRow('fg_pack_stock', pack.id as string, { status: 'RESERVED', ...auditFieldsForUpdate() })
    })
    txLog(
      'FG Penggabungan',
      'GAB-01',
      'gab-tambah',
      String(active.png_no),
      `Pack sisa ditambahkan: ${rows.length} baris (${rows.reduce((sum, row) => sum + Number(row.qty_pack), 0)} pack)`
    )
    return { png_no: active.png_no, appended: true }
  }
  const assigneeId = String(body.assignee_id ?? '')
  if (!assigneeId) fail('Pilih petugas WH FG dulu.')
  if (!roleCodesOf(assigneeId).includes('operator_wh_fg')) fail('Petugas harus Operator WH FG.')
  const destination = destinationOf(String(body.destination_code ?? ''))
  if (!destination) fail('Pilih area tujuan dulu.')
  const req = createRequest({
    kind: 'PACK',
    jenis: 'Pack',
    skuId,
    source: body.linked_request_id ? `Dari ${String(gabOf(body.linked_request_id).png_no)}` : 'Manual',
    destination: String(destination.code),
    operatorId: assigneeId,
    linked: body.linked_request_id ?? null
  })
  rows.forEach(pack => {
    insertItem(req, { pack })
    updateRow('fg_pack_stock', pack.id as string, { status: 'RESERVED', ...auditFieldsForUpdate() })
  })
  gabRun(req, 'ACT_GAB_APPROVE', ['ACT_GAB_ADMIN_FG'], { approved_by_id: currentUserId(), approved_at: nowIso() })
  txLog(
    'FG Penggabungan',
    'GAB-01',
    'gab-request',
    String(req.png_no),
    `Permintaan pack ${String(skuOf(skuId).code)} ke ${String(destination.name)} — petugas ${String(findRow('directus_users', assigneeId)?.first_name ?? '')}`
  )
  return { png_no: req.png_no, appended: false }
}

export function cancelPack(body: { request_id?: string; reason?: string }) {
  requireRole('Pembatalan permintaan pack hanya untuk Admin WH FG.', 'admin_wh_fg')
  const req = gabOf(body.request_id)
  if (req.kind !== 'PACK') fail('Hanya permintaan pack yang dapat dibatalkan.', 'ERR_INVALID_STATE')
  const status = statusCodeOf(req)
  if (!['GAB_READY', 'GAB_PICKING'].includes(status)) fail('Permintaan ini sudah tidak berjalan.', 'ERR_INVALID_STATE')
  const reason = String(body.reason ?? '').trim()
  if (reason.length < 3) fail('Alasan wajib diisi (minimal 3 huruf).', 'ERR_REASON_REQUIRED')
  gabRun(
    req,
    status === 'GAB_READY' ? 'ACT_GAB_CANCEL_READY' : 'ACT_GAB_CANCEL_PICKING',
    ['ACT_GAB_ADMIN_FG'],
    { cancel_reason: reason },
    reason
  )
  gabItems(req).forEach(item => {
    updateRow('merge_request_item', item.id as string, { item_status: 'RELEASED', ...auditFieldsForUpdate() })
    if (item.pack_stock_id)
      updateRow('fg_pack_stock', idOf(item.pack_stock_id), { status: 'IN_STOCK', ...auditFieldsForUpdate() })
  })
  txLog('FG Penggabungan', 'GAB-01', 'gab-cancel', String(req.png_no), `Permintaan pack dibatalkan — ${reason}`)
  return { png_no: req.png_no }
}

/* ============================================================ GAB-02 — operator */

function taskFor(requestId: unknown) {
  requireRole('Pengambilan dikerjakan Operator WH FG.', 'operator_wh_fg')
  const req = gabOf(requestId)
  const status = statusCodeOf(req)
  if (!['GAB_READY', 'GAB_PICKING'].includes(status))
    fail(
      status === 'GAB_PENDING' ? 'Permintaan belum disetujui Admin WH FG.' : 'Permintaan ini sudah tidak berjalan.',
      'ERR_INVALID_STATE'
    )
  const owner = idOf(req.operator_id)
  if (owner && owner !== currentUserId())
    fail(
      req.kind === 'PACK' ? 'Tugas pack ini milik petugas lain.' : 'Tugas ini sedang dikerjakan operator lain.',
      'ERR_NOT_TASK_OWNER',
      403
    )
  return req
}

function ensurePicking(req: LocalRow) {
  if (statusCodeOf(req) === 'GAB_READY')
    gabRun(req, 'ACT_GAB_START_PICK', ['ACT_GAB_OP_FG'], { operator_id: currentUserId() })
}

const reserved = (req: LocalRow) => gabItems(req).filter(item => item.item_status === 'RESERVED')

export function gabPickGate(body: { request_id?: string; rack_code?: string }) {
  const req = taskFor(body.request_id)
  const code = up(body.rack_code)
  const items = reserved(req)
  if (!palletByCode(code) && !containerByCode(code))
    fail(`${code} bukan pallet / container FG.`, 'ERR_LOCATION_REJECTED')
  const racks = [...new Set(items.map(rackCode))]
  if (!racks.includes(code))
    fail(
      racks.length ? `${code} di luar tugas ini. Pindai ${racks.join(' / ')}.` : `${code} di luar tugas ini.`,
      'ERR_LOCATION_REJECTED'
    )
  return { rack_code: code, pending: items.filter(item => rackCode(item) === code).length }
}

export function gabPickUnit(body: { request_id?: string; rack_code?: string; code?: string }) {
  const req = taskFor(body.request_id)
  if (req.kind !== 'ALM') fail('Permintaan pack diambil lewat jumlah pack, bukan scan unit.')
  const rack = up(body.rack_code)
  const pending = reserved(req).filter(item => rackCode(item) === rack)
  if (!pending.length) fail(`Tidak ada unit tersisa di ${rack}.`, 'ERR_NOT_IN_TASK')
  const item = pending.find(row => matchesLabel(labelOf(row.label_id), String(body.code ?? '')))
  if (!item) {
    const other = gabItems(req).find(
      row => row.label_id && matchesLabel(labelOf(row.label_id), String(body.code ?? ''))
    )
    if (other?.item_status === 'PICKED') fail('Unit ini sudah diambil.', 'ERR_DUPLICATE')
    if (other) fail(`Unit ini ada di ${rackCode(other)}, bukan ${rack}.`, 'ERR_NOT_IN_TASK')
    fail(`Bukan unit yang diminta di ${rack}. Ambil unit yang tertera pada tugas.`, 'ERR_NOT_IN_TASK')
  }
  ensurePicking(req)
  const label = labelOf(item.label_id)
  updateRow('merge_request_item', item.id as string, { item_status: 'PICKED', ...auditFieldsForUpdate() })
  updateRow('fg_label', label.id as string, { label_status: 'MERGE_PICKED', ...auditFieldsForUpdate() })
  if (item.from_pallet_id) setPalletOccupied(item.from_pallet_id, -1)
  return {
    label_no: label.label_no,
    remaining: pending.length - 1,
    remaining_total: reserved(req).length
  }
}

export function gabPickPack(body: { request_id?: string; container_code?: string; code?: string; qty?: number }) {
  const req = taskFor(body.request_id)
  if (req.kind !== 'PACK') fail('Permintaan ALM diambil lewat scan unit.')
  const container = up(body.container_code)
  const pending = reserved(req).filter(item => rackCode(item) === container)
  if (!pending.length) fail(`Tidak ada pack tersisa di ${container}.`, 'ERR_NOT_IN_TASK')
  const item = pending.find(row => {
    const pack = findRow('fg_pack_stock', idOf(row.pack_stock_id))
    return up(pack?.label_no) === up(body.code)
  })
  if (!item) {
    const other = gabItems(req).find(
      row => up(findRow('fg_pack_stock', idOf(row.pack_stock_id))?.label_no) === up(body.code)
    )
    if (other?.item_status === 'PICKED') fail('Pack ini sudah diambil.', 'ERR_DUPLICATE')
    fail(`Bukan pack yang diminta di ${container}.`, 'ERR_NOT_IN_TASK')
  }
  const planned = Number(item.qty_plan ?? 0)
  const qty = body.qty === undefined ? planned : Number(body.qty)
  if (!Number.isInteger(qty) || qty < 1 || qty > planned) fail(`Jumlah pack harus 1 – ${planned}.`)
  ensurePicking(req)
  updateRow('merge_request_item', item.id as string, {
    item_status: 'PICKED',
    qty_taken: qty,
    ...auditFieldsForUpdate()
  })
  if (qty < planned)
    txLog(
      'FG Penggabungan',
      'GAB-02',
      'gab-pack-shortage',
      String(req.png_no),
      `Selisih pack ${String(findRow('fg_pack_stock', idOf(item.pack_stock_id))?.label_no)}: diminta ${planned}, diambil ${qty}`
    )
  return { qty, planned, remaining_total: reserved(req).length }
}

function finish(req: LocalRow, areaCode: string, note: string, mode: 'ALM' | 'PACK') {
  const expected = String(req.destination_code)
  if (up(areaCode) !== up(expected)) fail(`Bukan area tujuan. Scan ${expected}.`, 'ERR_LOCATION_REJECTED')
  const items = gabItems(req)
  const picked = items.filter(item => item.item_status === 'PICKED')
  const left = items.filter(item => item.item_status === 'RESERVED')
  if (!picked.length) fail('Belum ada yang diambil. Scan rak dan barangnya dulu.', 'ERR_NOTHING_PICKED')
  if (left.length && !note.trim())
    fail(
      `${left.length} ${mode === 'ALM' ? 'unit' : 'baris pack'} belum diambil. Isi catatan untuk tetap membawa.`,
      'ERR_NOTE_REQUIRED'
    )
  const area = readTable('warehouse_zone').find(zone => up(zone.code) === up(expected))
  picked.forEach(item => {
    if (mode === 'ALM') {
      updateRow('merge_request_item', item.id as string, { item_status: 'AT_ALM', ...auditFieldsForUpdate() })
      updateRow('fg_label', idOf(item.label_id), {
        label_status: 'AT_ALM',
        zone_id: area?.id ?? null,
        pallet_id: null,
        container_id: null,
        ...auditFieldsForUpdate()
      })
    } else {
      updateRow('merge_request_item', item.id as string, { item_status: 'HANDED_OVER', ...auditFieldsForUpdate() })
      const pack = findRow('fg_pack_stock', idOf(item.pack_stock_id))
      if (pack) {
        const qty = Math.max(0, Number(pack.qty_pack ?? 0) - Number(item.qty_taken ?? item.qty_plan ?? 0))
        updateRow('fg_pack_stock', pack.id as string, {
          qty_pack: qty,
          status: qty > 0 ? 'IN_STOCK' : 'GONE',
          ...auditFieldsForUpdate()
        })
      }
    }
  })
  left.forEach(item => {
    updateRow('merge_request_item', item.id as string, { item_status: 'RELEASED', ...auditFieldsForUpdate() })
    if (item.label_id)
      updateRow('fg_label', idOf(item.label_id), { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
    if (item.pack_stock_id)
      updateRow('fg_pack_stock', idOf(item.pack_stock_id), { status: 'IN_STOCK', ...auditFieldsForUpdate() })
  })
  gabRun(req, 'ACT_GAB_COMPLETE', ['ACT_GAB_OP_FG'], {}, note.trim())
  txLog(
    'FG Penggabungan',
    'GAB-02',
    mode === 'ALM' ? 'gab-arrive' : 'gab-serah',
    String(req.png_no),
    `${picked.length} ${mode === 'ALM' ? String(req.jenis).toLowerCase() : 'baris pack'} ${String(skuOf(req.sku_id).code)} → ${String(area?.name ?? expected)}${note.trim() ? ` [CATATAN: ${note.trim()}]` : ''}`
  )
  return { png_no: req.png_no, arrived: picked.length, released: left.length }
}

export function gabArrive(body: { request_id?: string; area_code?: string; note?: string }) {
  const req = taskFor(body.request_id)
  if (req.kind !== 'ALM') fail('Permintaan pack diserahkan ke area tujuan produksi (Serahkan).')
  return finish(req, String(body.area_code ?? ''), String(body.note ?? ''), 'ALM')
}

export function gabHandOver(body: { request_id?: string; area_code?: string; note?: string }) {
  const req = taskFor(body.request_id)
  if (req.kind !== 'PACK') fail('Permintaan ALM selesai dengan scan Area ALM.')
  return finish(req, String(body.area_code ?? ''), String(body.note ?? ''), 'PACK')
}

/* ============================================================ KSL — saran & tugas */

type PalletContent = {
  id: string
  code: string
  sku_id: string
  jenis: 'Karton' | 'Bal'
  count: number
  keq: number
}

function palletContents(): PalletContent[] {
  return readTable('pallet')
    .filter(row => row.pallet_kind === 'FG' && row.sku_id && Number(row.occupied ?? 0) > 0)
    .map(row => {
      const jenis = (row.unit_type === 'Bal' ? 'Bal' : 'Karton') as 'Karton' | 'Bal'
      const count = Number(row.occupied)
      return {
        id: String(row.id),
        code: String(row.code),
        sku_id: idOf(row.sku_id),
        jenis,
        count,
        keq: round3(jenis === 'Bal' ? count / balPerKarton(row.sku_id) : count)
      }
    })
}

const busyPallets = () => {
  const busy = new Map<string, string>()
  readTable('consolidation_task')
    .filter(task => ACTIVE_KSL.includes(statusCodeOf(task)))
    .forEach(task => {
      busy.set(idOf(task.from_pallet_id), String(task.ksl_no))
      busy.set(idOf(task.to_pallet_id), String(task.ksl_no))
      readTable('consolidation_task_item')
        .filter(item => idOf(item.task_id) === task.id)
        .forEach(item => {
          busy.set(idOf(item.from_pallet_id), String(task.ksl_no))
          busy.set(idOf(item.to_pallet_id), String(task.ksl_no))
        })
    })
  // Pallet yang isinya sedang dipesan permintaan penggabungan (GAB) juga tidak boleh dikonsolidasi.
  readTable('fg_label')
    .filter(label => label.label_status === 'MERGE_RESERVED' && label.pallet_id)
    .forEach(label => busy.set(idOf(label.pallet_id), 'GAB'))
  return busy
}

const palletBatches = (palletId: string) => [
  ...new Set(
    readTable('fg_label')
      .filter(label => idOf(label.pallet_id) === palletId && label.label_status === 'IN_STOCK')
      .map(label => batchNoOf(label.batch_id))
      .filter(Boolean)
  )
]

/** Saran konsolidasi — dihitung ulang tiap dipanggil (prototype `palSaranKonsolidasi`). */
export function suggestions() {
  const groups = new Map<string, PalletContent[]>()
  palletContents()
    .filter(pallet => pallet.keq < PAL_REC)
    .forEach(pallet => {
      const key = `${pallet.sku_id}|${pallet.jenis}`
      groups.set(key, [...(groups.get(key) ?? []), pallet])
    })
  const busy = busyPallets()
  const result: Record<string, unknown>[] = []
  groups.forEach((pals, key) => {
    if (pals.length < 2) return
    const total = round3(pals.reduce((sum, pallet) => sum + pallet.keq, 0))
    const need = Math.max(1, Math.ceil(total / PAL_REC - EPS))
    const free = pals.length - need
    if (free < 1) return
    const sorted = [...pals].sort((a, b) => b.keq - a.keq || a.code.localeCompare(b.code))
    const targets = sorted.slice(0, need)
    const sources = sorted.slice(need)
    let rest = total
    const after = targets.map((pallet, index) => {
      const fill = index === targets.length - 1 ? rest : Math.min(PAL_REC, rest)
      rest = round3(rest - fill)
      return { code: pallet.code, keq: round3(fill) }
    })
    const [skuId, jenis] = key.split('|')
    const sku = skuOf(skuId)
    result.push({
      key,
      sku: { id: sku.id, code: sku.code, name: sku.name },
      jenis,
      bal_per_karton: balPerKarton(skuId),
      total_keq: total,
      need,
      free,
      pallets: sorted.map(pallet => ({
        code: pallet.code,
        keq: pallet.keq,
        count: pallet.count,
        role: targets.includes(pallet) ? 'TARGET' : 'SOURCE',
        batches: palletBatches(pallet.id)
      })),
      after,
      busy: pals.some(pallet => busy.has(pallet.id))
    })
  })
  return (result as { free: number; total_keq: number }[]).sort((a, b) => b.free - a.free || b.total_keq - a.total_keq)
}

type SuggestionRow = {
  sku: { id: string; code: string }
  jenis: 'Karton' | 'Bal'
  free: number
  busy: boolean
  pallets: { code: string; role: string; keq: number }[]
  after: { code: string; keq: number }[]
}

const kslOf = (id: unknown) => {
  const row = findRow('consolidation_task', String(id ?? ''))
  if (!row) fail('Tugas tidak ditemukan.', 'ERR_NOT_FOUND', 404)
  return row
}
const kslItems = (task: LocalRow) => readTable('consolidation_task_item').filter(item => idOf(item.task_id) === task.id)

function kslRun(task: LocalRow, action: string, actors: string[], patch: Record<string, unknown> = {}, reason = '') {
  kslFlow.run(task, action, actors, patch, reason)
  updateRow('consolidation_task', task.id as string, {
    task_status: statusCodeOf(kslOf(task.id)).replace('KSL_', '')
  })
}

export function createTasks(body: {
  sku_id?: string
  jenis?: string
  fill_limit?: number
  fill_reason?: string
  operator_id?: string
}) {
  requireRole('Tugas konsolidasi diterbitkan Admin atau Kadiv WH FG.', 'admin_wh_fg', 'kadiv_wh_fg')
  const suggestion = (suggestions() as unknown as SuggestionRow[]).find(
    row => row.sku.id === body.sku_id && row.jenis === body.jenis && !row.busy
  )
  if (!suggestion) fail('Saran sudah tidak berlaku — isinya berubah.', 'ERR_STALE_SUGGESTION')
  const limit = Number(body.fill_limit) === PAL_CAP ? PAL_CAP : PAL_REC
  const reason = String(body.fill_reason ?? '').trim()
  if (limit === PAL_CAP && !reason)
    fail(`Alasan wajib diisi kalau dipadatkan sampai ${PAL_CAP}.`, 'ERR_REASON_REQUIRED')
  const operatorId = String(body.operator_id ?? '')
  if (!roleCodesOf(operatorId).includes('operator_wh_fg')) fail('Pilih Operator WH FG.')
  const per = suggestion.jenis === 'Bal' ? 1 / balPerKarton(suggestion.sku.id) : 1
  const room: Record<string, number> = {}
  suggestion.pallets
    .filter(pallet => pallet.role === 'TARGET')
    .forEach(pallet => {
      room[pallet.code] = round3(limit - pallet.keq)
    })
  const moves: { label: LocalRow; from: string; to: string }[] = []
  let unplaced = 0
  suggestion.pallets
    .filter(pallet => pallet.role === 'SOURCE')
    .forEach(source => {
      const pallet = palletByCode(source.code) as LocalRow
      readTable('fg_label')
        .filter(label => idOf(label.pallet_id) === pallet.id && label.label_status === 'IN_STOCK')
        .sort((a, b) => String(a.label_no).localeCompare(String(b.label_no)))
        .forEach(label => {
          const target = Object.keys(room).find(code => room[code] >= per - EPS)
          if (!target) {
            unplaced += 1
            return
          }
          room[target] = round3(room[target] - per)
          moves.push({ label, from: source.code, to: target })
        })
    })
  if (!moves.length) fail('Tidak ada unit yang bisa dipindahkan.')
  const targets = [...new Set(moves.map(move => move.to))]
  const packageNo = targets.length > 1 ? nextDocNo('KSL-PKT', 'consolidation_task', 'package_no') : null
  const tasks = targets.map(target => {
    const mine = moves.filter(move => move.to === target)
    const id = newId()
    const kslNo = nextDocNo('KSL', 'consolidation_task', 'ksl_no')
    const { statusId, submissionId } = kslFlow.start(id)
    const task = insertRow('consolidation_task', {
      ...auditFieldsForCreate(),
      id,
      ksl_no: kslNo,
      sku_id: suggestion.sku.id,
      from_pallet_id: palletByCode(mine[0].from)?.id ?? null,
      to_pallet_id: palletByCode(target)?.id ?? null,
      target_fill: limit,
      fill_reason: limit === PAL_CAP ? reason : null,
      task_status: 'ASSIGNED',
      status_id: statusId,
      submission_id: submissionId,
      operator_id: operatorId,
      jenis: suggestion.jenis,
      package_no: packageNo,
      freed_count: suggestion.free,
      stop_reason: null
    })
    mine.forEach(move => {
      insertRow('consolidation_task_item', {
        ...auditFieldsForCreate(),
        id: newId(),
        task_id: id,
        label_id: move.label.id,
        unit_type: suggestion.jenis,
        from_pallet_id: palletByCode(move.from)?.id ?? null,
        to_pallet_id: palletByCode(target)?.id ?? null,
        final_pallet_id: null,
        item_status: 'WAITING',
        manual_reason: null
      })
      updateRow('fg_label', move.label.id as string, {
        label_status: 'CONSOLIDATION_RESERVED',
        ...auditFieldsForUpdate()
      })
    })
    txLog(
      'FG Konsolidasi',
      'KSL-01',
      'ksl-request',
      kslNo,
      `${suggestion.sku.code} — ${mine.length} unit dari ${[...new Set(mine.map(move => move.from))].join(', ')} → ${target} (membebaskan ${suggestion.free} pallet)`
    )
    return task
  })
  return {
    tasks: tasks.map(task => task.ksl_no),
    moved: moves.length,
    unplaced,
    freed: suggestion.free
  }
}

export function stopTask(body: { task_id?: string; reason?: string }) {
  requireRole('Tugas konsolidasi dihentikan Admin atau Kadiv WH FG.', 'admin_wh_fg', 'kadiv_wh_fg')
  const task = kslOf(body.task_id)
  const status = statusCodeOf(task)
  if (!ACTIVE_KSL.includes(status)) fail('Tugas ini sudah selesai atau dihentikan.', 'ERR_INVALID_STATE')
  const reason = String(body.reason ?? '').trim()
  if (!reason) fail('Alasan wajib diisi.', 'ERR_REASON_REQUIRED')
  const items = kslItems(task)
  const moved = items.filter(item => item.item_status === 'MOVED').length
  const released = items.filter(item => item.item_status !== 'MOVED')
  kslRun(
    task,
    status === 'KSL_ASSIGNED' ? 'ACT_KSL_STOP_ASSIGNED' : 'ACT_KSL_STOP_LOADED',
    ['ACT_KSL_ADMIN_FG', 'ACT_KSL_KADIV_FG'],
    { stop_reason: reason },
    reason
  )
  released.forEach(item => {
    updateRow('consolidation_task_item', item.id as string, { item_status: 'RELEASED', ...auditFieldsForUpdate() })
    const label = labelOf(item.label_id)
    if (label?.label_status === 'CONSOLIDATION_RESERVED')
      updateRow('fg_label', label.id as string, { label_status: 'IN_STOCK', ...auditFieldsForUpdate() })
  })
  txLog('FG Konsolidasi', 'KSL-01', 'ksl-stop', String(task.ksl_no), `Tugas dihentikan — ${reason}`)
  return { ksl_no: task.ksl_no, moved, released: released.length }
}

/* ============================================================ KSL-02 — operator */

function kslTaskFor(taskId: unknown) {
  requireRole('Konsolidasi dikerjakan Operator WH FG.', 'operator_wh_fg')
  const task = kslOf(taskId)
  if (idOf(task.operator_id) !== currentUserId()) fail('Tugas ini milik operator lain.', 'ERR_NOT_TASK_OWNER', 403)
  if (!ACTIVE_KSL.includes(statusCodeOf(task))) fail('Tugas ini sudah selesai atau dihentikan.', 'ERR_INVALID_STATE')
  return task
}

const itemPallet = (item: LocalRow) => String(palletOf(item.from_pallet_id)?.code ?? '')

export function sourceGate(body: { task_id?: string; pallet_code?: string }) {
  const task = kslTaskFor(body.task_id)
  const code = up(body.pallet_code)
  const waiting = kslItems(task).filter(item => item.item_status === 'WAITING')
  const sources = [...new Set(waiting.map(itemPallet))]
  if (code === up(palletOf(task.to_pallet_id)?.code))
    fail(`Pallet tujuan ${code} discan setelah semua unit dimuat dan tombol Bawa ditekan.`, 'ERR_LOCATION_REJECTED')
  if (!sources.includes(code))
    fail(
      `Pallet ${code} tidak punya tugas di sini. Pallet asal: ${sources.join(', ') || '—'}.`,
      'ERR_LOCATION_REJECTED'
    )
  return { pallet_code: code, waiting: waiting.filter(item => itemPallet(item) === code).length }
}

export function loadUnit(body: { task_id?: string; pallet_code?: string; code?: string; manual_reason?: string }) {
  const task = kslTaskFor(body.task_id)
  const pallet = up(body.pallet_code)
  const items = kslItems(task)
  const scanned = String(body.code ?? '')
  const waitingHere = items.filter(item => item.item_status === 'WAITING' && itemPallet(item) === pallet)
  const target = waitingHere.find(item => matchesLabel(labelOf(item.label_id), scanned))
  if (!target) {
    const other = items.find(item => matchesLabel(labelOf(item.label_id), scanned))
    if (other?.item_status === 'LOADED' || other?.item_status === 'MOVED')
      fail('Unit ini sudah dimuat.', 'ERR_DUPLICATE')
    if (other?.item_status === 'WAITING')
      fail(
        `Barang milik pallet ${itemPallet(other)}, bukan ${pallet}. Scan pallet ${itemPallet(other)} untuk pindah.`,
        'ERR_NOT_IN_TASK'
      )
    fail('Barang bukan bagian tugas ini.', 'ERR_NOT_IN_TASK')
  }
  updateRow('consolidation_task_item', target.id as string, {
    item_status: 'LOADED',
    manual_reason: body.manual_reason ? String(body.manual_reason) : null,
    ...auditFieldsForUpdate()
  })
  const left = items.filter(item => item.item_status === 'WAITING').length - 1
  return {
    label_no: labelOf(target.label_id).label_no,
    remaining_here: waitingHere.length - 1,
    remaining_total: left
  }
}

export function carry(body: { task_id?: string; note?: string }) {
  const task = kslTaskFor(body.task_id)
  const items = kslItems(task)
  const loaded = items.filter(item => item.item_status === 'LOADED')
  const waiting = items.filter(item => item.item_status === 'WAITING')
  if (!loaded.length) fail('Belum ada yang dimuat. Scan pallet asal dan unitnya dulu.', 'ERR_NOTHING_LOADED')
  const note = String(body.note ?? '').trim()
  if (waiting.length && !note)
    fail(`${waiting.length} unit belum dimuat. Isi catatan untuk tetap membawa.`, 'ERR_NOTE_REQUIRED')
  if (statusCodeOf(task) === 'KSL_ASSIGNED') kslRun(task, 'ACT_KSL_CARRY', ['ACT_KSL_OP_FG'], {}, note)
  if (waiting.length)
    txLog(
      'FG Konsolidasi',
      'KSL-02',
      'ksl-bawa-sisa',
      String(task.ksl_no),
      `${waiting.length} unit belum dimuat saat dibawa ke ${String(palletOf(task.to_pallet_id)?.code)} [CATATAN: ${note}]`
    )
  return { loaded: loaded.length, waiting: waiting.length }
}

/** Penjaga pallet tujuan: satu pallet satu SKU, Karton tidak dicampur Bal, kapasitas ≤ 24. */
function blocker(pallet: LocalRow, skuId: string, jenis: 'Karton' | 'Bal', count: number) {
  const held = Number(pallet.occupied ?? 0)
  if (pallet.sku_id && held > 0 && idOf(pallet.sku_id) !== skuId) return `berisi ${String(skuOf(pallet.sku_id)?.code)}`
  const heldJenis = pallet.unit_type === 'Bal' ? 'Bal' : 'Karton'
  if (pallet.sku_id && held > 0 && heldJenis !== jenis) return `pallet ${heldJenis === 'Bal' ? 'Bal lepas' : 'Karton'}`
  const per = jenis === 'Bal' ? 1 / balPerKarton(skuId) : 1
  const keq = held * (heldJenis === 'Bal' ? 1 / balPerKarton(skuId) : 1)
  if (keq + per * count > PAL_CAP + EPS) return `sudah ${round3(keq)} dari ${PAL_CAP}`
  return null
}

export function place(body: { task_id?: string; pallet_code?: string; reason?: string }) {
  const task = kslTaskFor(body.task_id)
  if (statusCodeOf(task) !== 'KSL_LOADED') fail('Tekan Bawa dulu sebelum scan pallet tujuan.', 'ERR_INVALID_STATE')
  const code = up(body.pallet_code)
  const pallet = palletByCode(code)
  if (!pallet)
    fail(
      `Kode ${code} bukan pallet FG. Pallet tujuan: ${String(palletOf(task.to_pallet_id)?.code)}.`,
      'ERR_LOCATION_REJECTED'
    )
  const items = kslItems(task)
  const loaded = items.filter(item => item.item_status === 'LOADED')
  if (items.some(item => itemPallet(item) === code))
    fail(
      `${code} adalah pallet asal. Pallet tujuan: ${String(palletOf(task.to_pallet_id)?.code)}.`,
      'ERR_LOCATION_REJECTED'
    )
  const skuId = idOf(task.sku_id)
  const jenis = (task.jenis === 'Bal' ? 'Bal' : 'Karton') as 'Karton' | 'Bal'
  const reason = String(body.reason ?? '').trim()
  const why = blocker(pallet, skuId, jenis, loaded.length)
  if (why)
    fail(
      `Pallet ${code} tidak bisa dipakai: ${why}. Satu pallet satu SKU, Karton tidak dicampur Bal, kapasitas dijaga.`,
      'ERR_LOCATION_REJECTED'
    )
  const planned = String(palletOf(task.to_pallet_id)?.code)
  if (code !== planned && !reason)
    fail(`Pallet ${code} bukan pallet tujuan tugas (${planned}). Alasan wajib.`, 'ERR_REASON_REQUIRED')
  loaded.forEach(item => {
    updateRow('fg_label', idOf(item.label_id), {
      label_status: 'IN_STOCK',
      pallet_id: pallet.id,
      zone_id: idOf(pallet.zone_id),
      ...auditFieldsForUpdate()
    })
    updateRow('consolidation_task_item', item.id as string, {
      item_status: 'MOVED',
      final_pallet_id: pallet.id,
      manual_reason: code !== planned ? reason : (item.manual_reason ?? null),
      ...auditFieldsForUpdate()
    })
    setPalletOccupied(item.from_pallet_id, -1)
  })
  setPalletOccupied(pallet.id, loaded.length, { sku_id: skuId, unit_type: jenis })
  const waiting = kslItems(task).filter(item => item.item_status === 'WAITING')
  if (!waiting.length) kslRun(task, 'ACT_KSL_COMPLETE', ['ACT_KSL_OP_FG'])
  const sources = [...new Set(loaded.map(itemPallet))]
  const freed = sources.filter(source => !Number(palletByCode(source)?.occupied ?? 0))
  txLog(
    'FG Konsolidasi',
    'KSL-02',
    'ksl-move',
    String(task.ksl_no),
    `${loaded.length} ${jenis} ${String(skuOf(skuId).code)} pindah ${sources.join(', ')} → ${code}${code !== planned ? ` — ${reason}` : ''}`
  )
  return { placed: loaded.length, pallet_code: code, task_done: !waiting.length, freed, waiting: waiting.length }
}

/* ============================================================ registrasi */

export function registerMergeConsolidationHandlers(mock: MockAdapter) {
  const base = '/api/v1/wms/merge-fg'
  const post = <T>(path: string, run: (body: T) => unknown) =>
    mock.onPost(`${base}/${path}`).reply(config => handle(() => run(parse<T>(config.data))))
  /* Kait baca: daftar / detail permintaan GAB menjalankan auto-approve lebih dulu. */
  registerReadHook(['merge_request', 'merge_request_item'], () => autoApproveSweep())
  mock.onGet(`${base}/alm-rule`).reply(() => handle(() => almRule()))
  post('pack-preview', packPreview)
  post('alm-start', almStart)
  post('approve', approve)
  post('reject', reject)
  post('create-pack', createPack)
  post('cancel-pack', cancelPack)
  post('pick-gate', gabPickGate)
  post('pick-unit', gabPickUnit)
  post('pick-pack', gabPickPack)
  post('arrive', gabArrive)
  post('hand-over', gabHandOver)
  post('suggestions', suggestions)
  post('create-tasks', createTasks)
  post('stop-task', stopTask)
  post('source-gate', sourceGate)
  post('load-unit', loadUnit)
  post('carry', carry)
  post('place', place)
}
